import api from './api';
import { 
  CertificationNotificationConfig,
  CertificationNotificationLog,
  CertificationExpirationDashboard,
  NotificationStats
} from '../types';

export interface NotificationConfig {
  id?: number;
  branch: string;
  department: string;
  manager_email: string | null;
  operations_manager_email: string | null;
  additional_email_1: string | null;
  additional_email_2: string | null;
  is_active: boolean;
}

export interface NotificationLogParams {
  page?: number;
  limit?: number;
  technician_id?: number;
  certification_id?: number;
  notification_type?: string;
  status?: string;
  start_date?: string;
  end_date?: string;
}

export interface DashboardParams {
  days?: number;
  branch?: string;
  department?: string;
  include_expired?: boolean;
}

class CertificationNotificationAPI {
  // Configuration Management
  async getConfigs(): Promise<CertificationNotificationConfig[]> {
    const response = await api.get('/certification-notifications/configs');
    return response.data;
  }

  async getConfig(branch: string, department: string): Promise<CertificationNotificationConfig> {
    const response = await api.get(`/certification-notifications/configs/${branch}/${department}`);
    return response.data;
  }

  async updateConfig(id: number, config: Partial<NotificationConfig>): Promise<CertificationNotificationConfig> {
    const response = await api.put(`/certification-notifications/configs/${id}`, config);
    return response.data;
  }

  // Notification Logs
  async getLogs(params?: NotificationLogParams): Promise<{
    logs: CertificationNotificationLog[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  }> {
    const response = await api.get('/certification-notifications/logs', { params });
    return response.data;
  }

  // Dashboard Data
  async getDashboard(params?: DashboardParams): Promise<CertificationExpirationDashboard> {
    const response = await api.get('/certification-notifications/dashboard', { params });
    return response.data;
  }

  // Statistics
  async getStats(start_date?: string, end_date?: string): Promise<NotificationStats> {
    const response = await api.get('/certification-notifications/stats', {
      params: { start_date, end_date }
    });
    return response.data;
  }

  // Manual Actions (Admin only)
  async triggerDailyCheck(): Promise<{ message: string }> {
    const response = await api.post('/certification-notifications/trigger/daily');
    return response.data;
  }

  async triggerWeeklyReport(): Promise<{ message: string }> {
    const response = await api.post('/certification-notifications/trigger/weekly');
    return response.data;
  }

  // Bulk Operations
  async sendBulkReminders(certificationIds: number[]): Promise<{
    success: number;
    failed: number;
    errors: string[];
  }> {
    const results = {
      success: 0,
      failed: 0,
      errors: [] as string[]
    };

    // Send reminders individually since we don't have a bulk endpoint
    for (const certId of certificationIds) {
      try {
        await api.post('/certification-notifications/send-reminder', {
          technician_certification_id: certId
        });
        results.success++;
      } catch (error: any) {
        results.failed++;
        results.errors.push(`Cert ID ${certId}: ${error.response?.data?.message || 'Failed'}`);
      }
    }

    return results;
  }
}

export default new CertificationNotificationAPI();
